import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Avatar,
  Paper,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  FlagOutlined as KraIcon,
  CheckCircleOutline as KpiIcon,
  AssessmentOutlined as TotalIcon,
  PendingActionsOutlined as PendingIcon,
  HighlightOffOutlined as RejectedIcon,
  ArrowForwardRounded as ArrowIcon,
  MailOutlineRounded as MailIcon,
  EventNoteOutlined as CycleIcon,
  TrendingUpRounded as MetricIcon,
} from '@mui/icons-material';
import {
  motion,
  AnimatePresence,
  MotionConfig,
  useMotionValue,
  useTransform,
  animate,
} from 'motion/react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

// Motion-enabled MUI components (presentation only)
const MotionBox = motion.create(Box);
const MotionCard = motion.create(Card);
const MotionPaper = motion.create(Paper);
const MotionButton = motion.create(Button);
const MotionAlert = motion.create(Alert);
const MotionTypography = motion.create(Typography);

const easeOutExpo = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

const cardVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1 },
};

// Parent container that staggers its children in as they mount.
const listVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};

const cycleColors = {
  Q1: 'primary',
  Q2: 'secondary',
  Q3: 'warning',
  Q4: 'error',
  Annual: 'success',
};

const statusColors = {
  draft: 'default',
  submitted: 'warning',
  approved: 'success',
  rejected: 'error',
};

// Resolves the existing palette keys above into a real colour. No new hues are
// introduced -- everything comes from the theme the rest of the app already uses.
const paletteColor = (theme, key) =>
  theme.palette[key]?.main || theme.palette.grey[600];

const initialsOf = (employee) =>
  `${employee?.firstName?.[0] || ''}${employee?.lastName?.[0] || ''}`.toUpperCase() || '?';

const countKpis = (kras) =>
  (kras || []).reduce((sum, kra) => sum + (kra.kpis || []).length, 0);

// Animated integer used by the summary strip.
const CountUp = ({ value }) => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => Math.round(v));
  useEffect(() => {
    const controls = animate(count, value, { duration: 0.9, ease: easeOutExpo });
    return () => controls.stop();
  }, [count, value]);
  return <motion.span>{rounded}</motion.span>;
};

// One tile in the summary strip.
const StatTile = ({ icon, label, value, colorKey, delay }) => {
  const theme = useTheme();
  const color = paletteColor(theme, colorKey);
  return (
    <MotionPaper
      elevation={0}
      variants={fadeUp}
      transition={{ duration: 0.45, delay, ease: easeOutExpo }}
      whileHover={{ y: -4 }}
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          flexShrink: 0,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          bgcolor: alpha(color, 0.12),
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
          <CountUp value={value} />
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
      </Box>
    </MotionPaper>
  );
};

const ManagerKpiProposals = () => {
  const theme = useTheme();
  const { getAuthHeaders } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const fetchProposals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/performance/proposals/team`, {
        headers: getAuthHeaders(),
      });
      setProposals(response.data.proposals || []);
    } catch (error) {
      console.error('Error fetching team KPI proposals:', error);
      toast.error('Failed to fetch team KPI/KRA proposals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProposals();
  }, []);

  const openDialog = (proposal) => {
    setSelectedProposal(proposal);
    setShowRejectForm(false);
    setRejectReason('');
  };

  const closeDialog = () => {
    setSelectedProposal(null);
    setShowRejectForm(false);
    setRejectReason('');
  };

  const handleApprove = async () => {
    try {
      setReviewing(true);
      await axios.post(
        `${API_URL}/performance/proposals/${selectedProposal._id}/review`,
        { decision: 'approved' },
        { headers: getAuthHeaders() }
      );
      toast.success('Proposal approved');
      closeDialog();
      fetchProposals();
    } catch (error) {
      console.error('Error approving proposal:', error);
      toast.error(error.response?.data?.message || 'Failed to approve proposal');
    } finally {
      setReviewing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejecting this proposal');
      return;
    }
    try {
      setReviewing(true);
      await axios.post(
        `${API_URL}/performance/proposals/${selectedProposal._id}/review`,
        { decision: 'rejected', reviewComments: rejectReason },
        { headers: getAuthHeaders() }
      );
      toast.success('Proposal rejected');
      closeDialog();
      fetchProposals();
    } catch (error) {
      console.error('Error rejecting proposal:', error);
      toast.error(error.response?.data?.message || 'Failed to reject proposal');
    } finally {
      setReviewing(false);
    }
  };

  if (loading) {
    return (
      <MotionConfig reducedMotion="user">
        <MotionBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 8,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            style={{ display: 'flex' }}
          >
            <CircularProgress />
          </motion.div>
          <MotionTypography
            variant="body2"
            color="text.secondary"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4, ease: easeOutExpo }}
          >
            Loading your team's proposals…
          </MotionTypography>
        </MotionBox>
      </MotionConfig>
    );
  }

  const pendingCount = proposals.filter((p) => p.status === 'submitted').length;
  const approvedCount = proposals.filter((p) => p.status === 'approved').length;
  const rejectedCount = proposals.filter((p) => p.status === 'rejected').length;

  const selectedCycleColor = selectedProposal
    ? paletteColor(theme, cycleColors[selectedProposal.reviewCycle] || 'default')
    : theme.palette.primary.main;

  return (
    <MotionConfig reducedMotion="user">
      <Box p={3}>
        {/* ---------- Header ---------- */}
        <MotionBox
          variants={listVariants}
          initial="hidden"
          animate="visible"
          sx={{ mb: 3 }}
        >
          <MotionTypography
            variant="h4"
            variants={fadeUp}
            transition={{ duration: 0.5, ease: easeOutExpo }}
            sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}
          >
            KPI/KRA Proposals - Team
          </MotionTypography>
          <MotionTypography
            variant="body2"
            color="text.secondary"
            variants={fadeUp}
            transition={{ duration: 0.5, ease: easeOutExpo }}
            sx={{ mt: 0.5, maxWidth: 640 }}
          >
            Review and approve the KPIs/KRAs your team members have proposed for upcoming review cycles.
          </MotionTypography>
          <MotionBox
            variants={fadeUp}
            transition={{ duration: 0.5, ease: easeOutExpo }}
            sx={{
              mt: 2,
              height: 3,
              width: 64,
              borderRadius: 2,
              bgcolor: 'primary.main',
            }}
          />
        </MotionBox>

        {/* ---------- Summary strip ---------- */}
        {proposals.length > 0 && (
          <MotionBox variants={listVariants} initial="hidden" animate="visible" sx={{ mb: 4 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <StatTile
                  icon={<TotalIcon fontSize="small" />}
                  label="Total proposals"
                  value={proposals.length}
                  colorKey="primary"
                  delay={0}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatTile
                  icon={<PendingIcon fontSize="small" />}
                  label="Awaiting review"
                  value={pendingCount}
                  colorKey="warning"
                  delay={0.06}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatTile
                  icon={<KpiIcon fontSize="small" />}
                  label="Approved"
                  value={approvedCount}
                  colorKey="success"
                  delay={0.12}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatTile
                  icon={<RejectedIcon fontSize="small" />}
                  label="Rejected"
                  value={rejectedCount}
                  colorKey="error"
                  delay={0.18}
                />
              </Grid>
            </Grid>
          </MotionBox>
        )}

        {/* ---------- Proposal cards ---------- */}
        <AnimatePresence mode="wait" initial={false}>
          {proposals.length === 0 ? (
            <MotionAlert
              key="empty"
              severity="info"
              variant="outlined"
              icon={<TotalIcon />}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: easeOutExpo }}
              sx={{ borderRadius: 3, py: 2 }}
            >
              No KPI/KRA proposals from your team yet.
            </MotionAlert>
          ) : (
            <motion.div
              key="list"
              variants={listVariants}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0 }}
            >
              <Grid container spacing={3}>
                {proposals.map((proposal) => {
                  const cycleKey = cycleColors[proposal.reviewCycle] || 'default';
                  const statusKey = statusColors[proposal.status] || 'default';
                  const cycleColor = paletteColor(theme, cycleKey);
                  const statusColor = paletteColor(theme, statusKey);
                  const kraCount = (proposal.kras || []).length;
                  const kpiCount = countKpis(proposal.kras);

                  return (
                    <Grid item xs={12} md={6} key={proposal._id}>
                      <MotionCard
                        layout
                        variants={cardVariants}
                        transition={{ duration: 0.5, ease: easeOutExpo }}
                        whileHover="hover"
                        initial="hidden"
                        elevation={0}
                        sx={{
                          position: 'relative',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'divider',
                          pl: '6px',
                        }}
                      >
                        {/* Cycle-coloured accent rail that widens on hover */}
                        <MotionBox
                          variants={{ hover: { width: 10 } }}
                          transition={{ duration: 0.3, ease: easeOutExpo }}
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 6,
                            bgcolor: cycleColor,
                          }}
                        />

                        {/* Soft wash that fades in on hover */}
                        <MotionBox
                          initial={{ opacity: 0 }}
                          variants={{ hover: { opacity: 1 } }}
                          transition={{ duration: 0.35, ease: easeOutExpo }}
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            pointerEvents: 'none',
                            background: `linear-gradient(135deg, ${alpha(cycleColor, 0.08)} 0%, ${alpha(cycleColor, 0)} 55%)`,
                          }}
                        />

                        <MotionBox
                          variants={{ hover: { y: -6 } }}
                          transition={{ duration: 0.35, ease: easeOutExpo }}
                          sx={{
                            position: 'relative',
                            p: 2.5,
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                          }}
                        >
                          {/* Identity row */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75 }}>
                            <MotionBox
                              variants={{ hover: { scale: 1.08, rotate: -3 } }}
                              transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                              sx={{ flexShrink: 0 }}
                            >
                              <Avatar
                                sx={{
                                  width: 48,
                                  height: 48,
                                  fontSize: 16,
                                  fontWeight: 700,
                                  color: cycleColor,
                                  bgcolor: alpha(cycleColor, 0.14),
                                }}
                              >
                                {initialsOf(proposal.employeeId)}
                              </Avatar>
                            </MotionBox>

                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
                                {proposal.employeeId?.firstName} {proposal.employeeId?.lastName}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, minWidth: 0 }}>
                                <MailIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {proposal.employeeId?.email}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Status pill */}
                            <Chip
                              label={proposal.status}
                              size="small"
                              sx={{
                                flexShrink: 0,
                                textTransform: 'capitalize',
                                fontWeight: 600,
                                color: statusColor,
                                bgcolor: alpha(statusColor, 0.12),
                                border: '1px solid',
                                borderColor: alpha(statusColor, 0.3),
                              }}
                            />
                          </Box>

                          {/* Meta row: cycle + counts */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: 1,
                              mt: 2.25,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 2,
                                color: cycleColor,
                                bgcolor: alpha(cycleColor, 0.1),
                              }}
                            >
                              <CycleIcon sx={{ fontSize: 16 }} />
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {proposal.reviewCycle} {proposal.reviewYear}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                color: 'text.secondary',
                              }}
                            >
                              <KraIcon sx={{ fontSize: 16 }} />
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {kraCount} KRA{kraCount === 1 ? '' : 's'}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                color: 'text.secondary',
                              }}
                            >
                              <MetricIcon sx={{ fontSize: 16 }} />
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {kpiCount} KPI{kpiCount === 1 ? '' : 's'}
                              </Typography>
                            </Box>
                          </Box>

                          {/* KRA name preview */}
                          {kraCount > 0 && (
                            <Stack spacing={0.75} sx={{ mt: 2 }}>
                              {(proposal.kras || []).slice(0, 2).map((kra, idx) => (
                                <MotionBox
                                  key={idx}
                                  initial={{ opacity: 0, x: -8 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ duration: 0.35, delay: 0.15 + idx * 0.06, ease: easeOutExpo }}
                                  sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
                                >
                                  <Box
                                    sx={{
                                      width: 5,
                                      height: 5,
                                      borderRadius: '50%',
                                      flexShrink: 0,
                                      bgcolor: alpha(cycleColor, 0.7),
                                    }}
                                  />
                                  <Typography variant="body2" color="text.secondary" noWrap>
                                    {kra.name}
                                  </Typography>
                                </MotionBox>
                              ))}
                              {kraCount > 2 && (
                                <Typography variant="caption" color="text.disabled" sx={{ pl: 1.75 }}>
                                  +{kraCount - 2} more
                                </Typography>
                              )}
                            </Stack>
                          )}

                          <Box sx={{ flexGrow: 1, minHeight: 16 }} />

                          <Divider sx={{ mb: 1.75 }} />

                          <MotionButton
                            fullWidth
                            variant="contained"
                            disableElevation
                            onClick={() => openDialog(proposal)}
                            whileTap={{ scale: 0.98 }}
                            transition={{ type: 'spring', stiffness: 420, damping: 30 }}
                            endIcon={
                              <MotionBox
                                component="span"
                                variants={{ hover: { x: 4 } }}
                                transition={{ type: 'spring', stiffness: 420, damping: 26 }}
                                sx={{ display: 'flex' }}
                              >
                                <ArrowIcon fontSize="small" />
                              </MotionBox>
                            }
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                              py: 1,
                            }}
                          >
                            Review
                          </MotionButton>
                        </MotionBox>
                      </MotionCard>
                    </Grid>
                  );
                })}
              </Grid>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ---------- Review dialog ---------- */}
        <Dialog
          open={!!selectedProposal}
          onClose={closeDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          <DialogTitle sx={{ p: 0 }}>
            <Box
              sx={{
                position: 'relative',
                px: 3,
                pt: 3,
                pb: 2.5,
                bgcolor: alpha(selectedCycleColor, 0.07),
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <MotionBox
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.6, ease: easeOutExpo }}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  transformOrigin: 'left',
                  bgcolor: selectedCycleColor,
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MotionBox
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 380, damping: 24 }}
                  sx={{ flexShrink: 0 }}
                >
                  <Avatar
                    sx={{
                      width: 52,
                      height: 52,
                      fontSize: 18,
                      fontWeight: 700,
                      color: selectedCycleColor,
                      bgcolor: alpha(selectedCycleColor, 0.16),
                    }}
                  >
                    {initialsOf(selectedProposal?.employeeId)}
                  </Avatar>
                </MotionBox>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.06em' }}>
                    KPI/KRA PROPOSAL
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
                    {selectedProposal?.employeeId?.firstName} {selectedProposal?.employeeId?.lastName}
                  </Typography>
                  {selectedProposal?.employeeId?.email && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {selectedProposal.employeeId.email}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 3 }}>
            {selectedProposal && (
              <>
                <MotionBox
                  display="flex"
                  gap={1}
                  flexWrap="wrap"
                  mb={2.5}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, ease: easeOutExpo }}
                >
                  <Chip
                    label={`${selectedProposal.reviewCycle} ${selectedProposal.reviewYear}`}
                    color={cycleColors[selectedProposal.reviewCycle] || 'default'}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label={selectedProposal.status}
                    color={statusColors[selectedProposal.status] || 'default'}
                    size="small"
                    sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                  />
                  <Chip
                    variant="outlined"
                    size="small"
                    icon={<KraIcon sx={{ fontSize: 15 }} />}
                    label={`${(selectedProposal.kras || []).length} KRAs`}
                  />
                  <Chip
                    variant="outlined"
                    size="small"
                    icon={<MetricIcon sx={{ fontSize: 15 }} />}
                    label={`${countKpis(selectedProposal.kras)} KPIs`}
                  />
                </MotionBox>

                {(selectedProposal.kras || []).length > 0 ? (
                  <motion.div variants={listVariants} initial="hidden" animate="visible">
                    {selectedProposal.kras.map((kra, kraIdx) => (
                      <motion.div
                        key={kraIdx}
                        variants={fadeUp}
                        transition={{ duration: 0.4, ease: easeOutExpo }}
                      >
                        <Accordion
                          disableGutters
                          elevation={0}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            mb: 1.25,
                            borderRadius: 2,
                            overflow: 'hidden',
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': {
                              borderColor: alpha(selectedCycleColor, 0.4),
                            },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              px: 2,
                              '&.Mui-expanded': { bgcolor: alpha(selectedCycleColor, 0.05) },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  flexShrink: 0,
                                  borderRadius: 1.5,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: 'primary.main',
                                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                                }}
                              >
                                {kraIdx + 1}
                              </Box>
                              <KraIcon color="primary" fontSize="small" />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                {kra.name}
                              </Typography>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`${(kra.kpis || []).length} KPI${(kra.kpis || []).length === 1 ? '' : 's'}`}
                                sx={{ flexShrink: 0, height: 22, fontSize: 11 }}
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                            {kra.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                {kra.description}
                              </Typography>
                            )}
                            <Stack spacing={1}>
                              {(kra.kpis || []).map((kpi, kpiIdx) => (
                                <MotionBox
                                  key={kpiIdx}
                                  initial={{ opacity: 0, x: -12 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ duration: 0.32, delay: kpiIdx * 0.05, ease: easeOutExpo }}
                                  whileHover={{ x: 3 }}
                                  sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    alignItems: 'flex-start',
                                    p: 1.5,
                                    bgcolor: 'grey.50',
                                    borderRadius: 2,
                                    borderLeft: '3px solid',
                                    borderColor: 'success.main',
                                  }}
                                >
                                  <KpiIcon sx={{ color: 'success.main', mt: 0.2 }} fontSize="small" />
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{kpi.name}</Typography>
                                    {kpi.description && (
                                      <Typography variant="body2" color="text.secondary">{kpi.description}</Typography>
                                    )}
                                  </Box>
                                </MotionBox>
                              ))}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      </motion.div>
                    ))}
                  </motion.div>
                ) : (
                  <Box
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      borderRadius: 2,
                      border: '1px dashed',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">No KRAs in this proposal.</Typography>
                  </Box>
                )}

                {selectedProposal.status === 'submitted' && (
                  <Box mt={3}>
                    <AnimatePresence mode="wait" initial={false}>
                      {!showRejectForm ? (
                        <MotionBox
                          key="actions"
                          display="flex"
                          gap={1.5}
                          justifyContent="flex-end"
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.25, ease: easeOutExpo }}
                        >
                          <MotionButton
                            variant="outlined"
                            color="error"
                            onClick={() => setShowRejectForm(true)}
                            disabled={reviewing}
                            whileHover={reviewing ? undefined : { scale: 1.03 }}
                            whileTap={reviewing ? undefined : { scale: 0.97 }}
                            transition={{ type: 'spring', stiffness: 420, damping: 30 }}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 2.5 }}
                          >
                            Reject
                          </MotionButton>
                          <MotionButton
                            variant="contained"
                            color="primary"
                            disableElevation
                            onClick={handleApprove}
                            disabled={reviewing}
                            whileHover={reviewing ? undefined : { scale: 1.03 }}
                            whileTap={reviewing ? undefined : { scale: 0.97 }}
                            transition={{ type: 'spring', stiffness: 420, damping: 30 }}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
                          >
                            {reviewing ? <CircularProgress size={24} /> : 'Approve'}
                          </MotionButton>
                        </MotionBox>
                      ) : (
                        <MotionBox
                          key="reject-form"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: easeOutExpo }}
                          sx={{ overflow: 'hidden' }}
                        >
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: alpha(theme.palette.error.main, 0.3),
                              bgcolor: alpha(theme.palette.error.main, 0.04),
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                              <RejectedIcon color="error" fontSize="small" />
                              <Typography variant="subtitle1" color="error" sx={{ fontWeight: 600 }}>
                                Reject Proposal
                              </Typography>
                            </Box>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              label="Reason for rejection *"
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              placeholder="Explain what should be revised..."
                              required
                              sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                            />
                            <Box display="flex" gap={1.5} justifyContent="flex-end" mt={2}>
                              <MotionButton
                                variant="outlined"
                                onClick={() => setShowRejectForm(false)}
                                disabled={reviewing}
                                whileHover={reviewing ? undefined : { scale: 1.03 }}
                                whileTap={reviewing ? undefined : { scale: 0.97 }}
                                transition={{ type: 'spring', stiffness: 420, damping: 30 }}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                              >
                                Cancel
                              </MotionButton>
                              <MotionButton
                                variant="contained"
                                color="error"
                                disableElevation
                                onClick={handleReject}
                                disabled={reviewing}
                                whileHover={reviewing ? undefined : { scale: 1.03 }}
                                whileTap={reviewing ? undefined : { scale: 0.97 }}
                                transition={{ type: 'spring', stiffness: 420, damping: 30 }}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                              >
                                {reviewing ? <CircularProgress size={24} /> : 'Confirm Rejection'}
                              </MotionButton>
                            </Box>
                          </Box>
                        </MotionBox>
                      )}
                    </AnimatePresence>
                  </Box>
                )}
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeDialog} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MotionConfig>
  );
};

export default ManagerKpiProposals;
